import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, VendorOrder, VendorOrdersQuery } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ORDER_STATUSES, ANALYTICS_STATUSES, SLIP_STATUSES, formatMoney, periodRange } from '../../utils/vendorPeriods';
import { PeriodValue, VendorPeriodBar } from './VendorPeriodBar';
import { PackingSlipModal } from './PackingSlipModal';
import { DocumentModal } from '../documents/DocumentModal';

const PAGE = 25;
const PAYMENT_STATUSES = ['unpaid', 'partial', 'paid', 'refunded'];
const STATUS_COLORS: Record<string, string> = {
  pending: '#9ca3af', placed: '#3b82f6', confirmed: '#6366f1', processing: '#f59e0b', shipped: '#0ea5e9', delivered: '#16a34a', cancelled: '#dc2626',
};

interface Props {
  vendor: { _id: string; name?: string; phone?: string; email?: string };
  products: Array<{ _id: string; name: string }>;
  onOpenOrder: (orderId: string) => void;
}

/** A vendor's orders (only its lines), filters, selection and packing slips. */
export const VendorOrdersTab: React.FC<Props> = ({ vendor, products, onOpenOrder }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();

  const [period, setPeriod] = useState<PeriodValue>({ period: 'all' });
  // Default: orders still to fulfil (what packing slips are for).
  const [statuses, setStatuses] = useState<string[]>(SLIP_STATUSES);
  const [payments, setPayments] = useState<string[]>([]);
  const [slip, setSlip] = useState<'yes' | 'no' | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [slipScope, setSlipScope] = useState<'selected' | 'all' | null>(null);
  const [poScope, setPoScope] = useState<'selected' | 'all' | null>(null);

  const query: VendorOrdersQuery = useMemo(() => ({
    ...periodRange(period.period, period),
    statuses,
    paymentStatuses: payments,
    slip,
    productId,
    search: search.trim() || undefined,
  }), [period, statuses, payments, slip, productId, search]);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getVendorOrders(vendor._id, { ...query, limit: PAGE, skip });
      if (res.statusCode === 1000 && res.data) {
        const page = res.data.orders || [];
        setOrders((prev) => (skip ? [...prev, ...page] : page));
        setTotal(res.data.total || 0);
      } else {
        setError((res as any).message || t('vendors.orders.loadFailed'));
      }
    } catch (e: any) {
      setError(e?.message || t('vendors.orders.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [vendor._id, query]);

  useEffect(() => {
    const timer = setTimeout(() => { setSelected([]); load(0); }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [load]);

  const toggle = (list: string[], set: (v: string[]) => void, value: string) => set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  const toggleSelected = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allShownSelected = orders.length > 0 && orders.every((o) => selected.includes(o._id));
  const activeFilters = (payments.length ? 1 : 0) + (slip ? 1 : 0) + (productId ? 1 : 0)
    + (statuses.length !== SLIP_STATUSES.length || !SLIP_STATUSES.every((s) => statuses.includes(s)) ? 1 : 0);

  const chip = (active: boolean, label: string, onPress: () => void, color?: string) => (
    <TouchableOpacity key={label} style={[styles.chip, active && { backgroundColor: color || (colors.primary as string), borderColor: color || (colors.primary as string) }]} onPress={onPress} activeOpacity={0.7}>
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  const date = (iso: string) => { try { return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; } };

  return (
    <View style={styles.wrap}>
      <VendorPeriodBar value={period} onChange={setPeriod} />

      <View style={[styles.searchRow, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="search" size={16} color={colors.text.secondary as string} />
          <TextInput style={[styles.searchInput, { textAlign: rtl.textStart }]} value={search} onChangeText={setSearch} placeholder={t('vendors.orders.search')} placeholderTextColor={colors.text.disabled as string} />
        </View>
        <TouchableOpacity style={[styles.filterButton, (showFilters || activeFilters > 0) && styles.filterButtonActive, { flexDirection: rtl.rowDirection }]} onPress={() => setShowFilters((v) => !v)}>
          <Ionicons name="options-outline" size={16} color={(showFilters || activeFilters > 0) ? (colors.white as string) : (colors.text.primary as string)} />
          <Text style={[styles.filterText, (showFilters || activeFilters > 0) && styles.chipTextActive]}>{activeFilters ? t('vendors.orders.filtersWithCount', { count: activeFilters }) : t('vendors.orders.filters')}</Text>
        </TouchableOpacity>
      </View>

      {showFilters && (
        <View style={styles.filters}>
          <Text style={[styles.filterLabel, { textAlign: rtl.textStart }]}>{t('vendors.orders.status')}</Text>
          <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
            {ORDER_STATUSES.map((s) => chip(statuses.includes(s), t(`orders.status.${s}` as any), () => toggle(statuses, (v) => setStatuses(v.length ? v : SLIP_STATUSES), s), STATUS_COLORS[s]))}
          </View>
          <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
            {chip(false, t('vendors.orders.toFulfil'), () => setStatuses(SLIP_STATUSES))}
            {chip(false, t('vendors.orders.allPlaced'), () => setStatuses(ANALYTICS_STATUSES))}
          </View>
          <Text style={[styles.filterLabel, { textAlign: rtl.textStart }]}>{t('vendors.orders.payment')}</Text>
          <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
            {PAYMENT_STATUSES.map((s) => chip(payments.includes(s), t(`orders.payment.${s}` as any), () => toggle(payments, setPayments, s)))}
          </View>
          <Text style={[styles.filterLabel, { textAlign: rtl.textStart }]}>{t('vendors.orders.slip')}</Text>
          <View style={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
            {chip(slip === 'no', t('vendors.orders.slipNo'), () => setSlip(slip === 'no' ? undefined : 'no'))}
            {chip(slip === 'yes', t('vendors.orders.slipYes'), () => setSlip(slip === 'yes' ? undefined : 'yes'))}
          </View>
          {products.length > 0 && (
            <>
              <Text style={[styles.filterLabel, { textAlign: rtl.textStart }]}>{t('vendors.orders.product')}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.chipsRow, { flexDirection: rtl.rowDirection }]}>
                {products.map((p) => chip(productId === p._id, p.name, () => setProductId(productId === p._id ? undefined : p._id)))}
              </ScrollView>
            </>
          )}
        </View>
      )}

      <View style={[styles.toolbar, { flexDirection: rtl.rowDirection }]}>
        <TouchableOpacity style={[styles.selectAll, { flexDirection: rtl.rowDirection }]} onPress={() => setSelected(allShownSelected ? [] : orders.map((o) => o._id))} disabled={!orders.length}>
          <Ionicons name={allShownSelected ? 'checkbox' : 'square-outline'} size={18} color={colors.primary as string} />
          <Text style={styles.toolbarText}>{selected.length ? t('vendors.orders.selected', { count: selected.length }) : t('vendors.orders.count', { count: total })}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.poButton, { flexDirection: rtl.rowDirection }, !total && styles.disabled]}
          disabled={!total}
          onPress={() => setPoScope(selected.length ? 'selected' : 'all')}
          accessibilityLabel={t('documents.type.purchase_order')}
        >
          <Ionicons name="cart-outline" size={16} color={colors.primary as string} />
          <Text style={styles.poButtonText}>{t('documents.type.purchase_order')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.slipButton, { flexDirection: rtl.rowDirection }, !total && styles.disabled]}
          disabled={!total}
          onPress={() => setSlipScope(selected.length ? 'selected' : 'all')}
        >
          <Ionicons name="print-outline" size={16} color={colors.white as string} />
          <Text style={styles.slipButtonText}>{selected.length ? t('vendors.orders.slipsSelected', { count: selected.length }) : t('vendors.orders.slipsAll')}</Text>
        </TouchableOpacity>
      </View>

      {!!error && <Text style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text>}

      {orders.map((o) => {
        const isSelected = selected.includes(o._id);
        return (
          <TouchableOpacity key={o._id} style={[styles.orderCard, isSelected && styles.orderCardSelected]} onPress={() => onOpenOrder(o._id)} activeOpacity={0.8}>
            <View style={[styles.orderTop, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity onPress={() => toggleSelected(o._id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} accessibilityLabel={t('vendors.orders.select')}>
                <Ionicons name={isSelected ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
              </TouchableOpacity>
              <View style={styles.orderMain}>
                <Text style={[styles.orderNumber, { textAlign: rtl.textStart }]}>{o.orderNumber}</Text>
                <Text style={[styles.orderMeta, { textAlign: rtl.textStart }]} numberOfLines={1}>{[date(o.orderDate), o.customer?.name].filter(Boolean).join(' · ')}</Text>
              </View>
              <View style={[styles.badge, { backgroundColor: (STATUS_COLORS[o.orderStatus] || '#9ca3af') + '22' }]}>
                <Text style={[styles.badgeText, { color: STATUS_COLORS[o.orderStatus] || '#6b7280' }]}>{t(`orders.status.${o.orderStatus}` as any)}</Text>
              </View>
            </View>
            {o.lines.map((l, i) => (
              <View key={i} style={[styles.line, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.lineQty}>{l.quantity}×</Text>
                <Text style={[styles.lineName, { textAlign: rtl.textStart }]} numberOfLines={1}>{l.name}{l.variant ? ` · ${l.variant}` : ''}</Text>
                <Text style={styles.lineTotal}>{formatMoney(l.totalPrice, o.currency, locale)}</Text>
              </View>
            ))}
            <View style={[styles.orderFoot, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.footText}>{t('vendors.orders.units', { count: o.units })}{o.otherVendorLines ? ` · ${t('vendors.orders.otherLines', { count: o.otherVendorLines })}` : ''}</Text>
              {o.slip
                ? <Text style={[styles.footText, styles.slipDone]}>✓ {t('vendors.orders.slipPrinted', { date: date(o.slip.generatedAt) })}</Text>
                : null}
              <Text style={styles.subtotal}>{formatMoney(o.subtotal, o.currency, locale)}</Text>
            </View>
          </TouchableOpacity>
        );
      })}

      {loading && <ActivityIndicator style={{ marginVertical: spacing.md }} color={colors.primary as string} />}
      {!loading && !orders.length && !error && (
        <View style={styles.empty}>
          <Ionicons name="receipt-outline" size={40} color={colors.text.disabled as string} />
          <Text style={styles.emptyText}>{t('vendors.orders.empty')}</Text>
        </View>
      )}
      {!loading && orders.length < total && (
        <TouchableOpacity style={styles.more} onPress={() => load(orders.length)}>
          <Text style={styles.moreText}>{t('vendors.orders.loadMore', { shown: orders.length, total })}</Text>
        </TouchableOpacity>
      )}

      <DocumentModal
        sendSource={{ vendorId: vendor._id }}
        visible={!!poScope}
        types={['purchase_order']}
        scopeLabel={poScope === 'selected' ? t('vendors.slips.scopeSelected', { count: selected.length }) : t('vendors.slips.scopeAll', { count: total })}
        recipient={vendor}
        purchaseOrderFields
        onClose={() => setPoScope(null)}
        onGenerate={async (req) => {
          const res = await apiService.generatePurchaseOrder(vendor._id, {
            ...(poScope === 'selected' ? { orderIds: selected } : { statuses: query.statuses, from: query.from, to: query.to }),
            templateId: req.templateId, language: req.language, userLocale: req.userLocale, notes: req.notes, deliverBy: req.deliverBy,
          });
          if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || res.errors?.[0]?.message || t('documents.failed'));
          return res.data;
        }}
      />

      <PackingSlipModal
        sendSource={{ vendorId: vendor._id }}
        visible={!!slipScope}
        scopeLabel={slipScope === 'selected' ? t('vendors.slips.scopeSelected', { count: selected.length }) : t('vendors.slips.scopeAll', { count: total })}
        multipleOrders={slipScope === 'selected' ? selected.length > 1 : total > 1}
        vendor={vendor}
        onClose={() => setSlipScope(null)}
        onGenerate={async (options) => {
          const body = slipScope === 'selected' ? { orderIds: selected, ...options } : { ...query, ...options };
          const res = await apiService.generateVendorPackingSlips(vendor._id, body);
          if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || t('vendors.slips.failed'));
          load(0);
          return res.data;
        }}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrap: { gap: spacing.sm },
  searchRow: { alignItems: 'center', gap: spacing.sm },
  searchBox: { flex: 1, alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.md, paddingHorizontal: spacing.sm, backgroundColor: colors.background.surface },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.sm, color: colors.text.primary },
  filterButton: { alignItems: 'center', gap: 6, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  filterButtonActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  filterText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  filters: { gap: spacing.xs, padding: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  filterLabel: { fontSize: typography.fontSize.xs, fontWeight: '700', color: colors.text.secondary, marginTop: spacing.xs },
  chipsRow: { gap: spacing.xs, flexWrap: 'wrap' },
  chip: { paddingVertical: 5, paddingHorizontal: spacing.sm + 2, borderRadius: 999, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipText: { fontSize: typography.fontSize.xs, color: colors.text.primary, fontWeight: '600' },
  chipTextActive: { color: colors.white },
  toolbar: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, marginTop: spacing.xs },
  selectAll: { alignItems: 'center', gap: spacing.xs },
  toolbarText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontWeight: '600' },
  slipButton: { alignItems: 'center', gap: 6, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: spacing.borderRadius.md, backgroundColor: colors.primary },
  slipButtonText: { color: colors.white, fontSize: typography.fontSize.sm, fontWeight: '700' },
  poButton: { alignItems: 'center', gap: 6, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.primary, marginStart: 'auto' },
  poButtonText: { color: colors.primary, fontSize: typography.fontSize.sm, fontWeight: '700' },
  disabled: { opacity: 0.5 },
  error: { color: colors.error, fontSize: typography.fontSize.sm },
  orderCard: { padding: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, gap: 6 },
  orderCardSelected: { borderColor: colors.primary },
  orderTop: { alignItems: 'center', gap: spacing.sm },
  orderMain: { flex: 1 },
  orderNumber: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.primary },
  orderMeta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  badge: { paddingVertical: 3, paddingHorizontal: spacing.sm, borderRadius: 999 },
  badgeText: { fontSize: 11, fontWeight: '700' },
  line: { alignItems: 'center', gap: spacing.xs },
  lineQty: { fontSize: typography.fontSize.xs, fontWeight: '700', color: colors.text.primary, minWidth: 28 },
  lineName: { flex: 1, fontSize: typography.fontSize.xs, color: colors.text.primary },
  lineTotal: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  orderFoot: { alignItems: 'center', gap: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border, paddingTop: 6, marginTop: 2 },
  footText: { fontSize: 11, color: colors.text.secondary },
  slipDone: { color: colors.success },
  subtotal: { marginStart: 'auto', fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.primary },
  empty: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xl },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  more: { alignItems: 'center', paddingVertical: spacing.md },
  moreText: { color: colors.primary, fontWeight: '700', fontSize: typography.fontSize.sm },
});
